import { useEffect } from "react";
import { useProducts } from "@/hooks/useProducts";

/**
 * ProductJsonLd
 * -------------
 * Injects a single <script type="application/ld+json"> into <head>
 * containing one <Product> entry per live SKU pulled from MongoDB via
 * /api/products.
 *
 * Each Product carries name, image, description, brand, sku, category and
 * an Offer (price, currency, availability, seller, priceValidUntil) so
 * Google Shopping can build a rich card from a single landing page hit.
 *
 * Renders nothing visually. Cleans up its tag on unmount.
 */
const SITE_ORIGIN = "https://substituteteacher.wallofentertainment.com";
const BRAND_ID = `${SITE_ORIGIN}/#org`;
const SELLER_ID = `${SITE_ORIGIN}/#revurge`;

function nextYearISO() {
  const d = new Date();
  d.setFullYear(d.getFullYear() + 1);
  return d.toISOString().slice(0, 10);
}

function buildPayload(products) {
  const priceValidUntil = nextYearISO();
  const items = products.map((p, idx) => ({
    "@type": "ListItem",
    position: idx + 1,
    item: {
      "@type": "Product",
      "@id": `${SITE_ORIGIN}/#product-${p.id}`,
      sku: p.id,
      mpn: p.id,
      name: p.name,
      description: p.description || `${p.name} — official Substitute Teacher merch.`,
      image: [p.image].filter(Boolean),
      url: `${SITE_ORIGIN}/#shop`,
      category: p.category,
      brand: { "@type": "Brand", "@id": BRAND_ID, name: "Wall of Entertainment" },
      offers: {
        "@type": "Offer",
        price: Number(p.price).toFixed(2),
        priceCurrency: (p.currency || "USD").toUpperCase(),
        availability: "https://schema.org/InStock",
        itemCondition: "https://schema.org/NewCondition",
        url: `${SITE_ORIGIN}/#shop`,
        priceValidUntil,
        seller: { "@type": "Organization", "@id": SELLER_ID, name: "Revurge" },
        shippingDetails: {
          "@type": "OfferShippingDetails",
          shippingRate: {
            "@type": "MonetaryAmount",
            value: "6.50",
            currency: "USD",
          },
          shippingDestination: {
            "@type": "DefinedRegion",
            addressCountry: "US",
          },
          deliveryTime: {
            "@type": "ShippingDeliveryTime",
            handlingTime: { "@type": "QuantitativeValue", minValue: 2, maxValue: 4, unitCode: "DAY" },
            transitTime: { "@type": "QuantitativeValue", minValue: 3, maxValue: 7, unitCode: "DAY" },
          },
        },
      },
    },
  }));

  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "@id": `${SITE_ORIGIN}/#product-catalog`,
    name: "Substitute Teacher — Official Merch Catalog",
    itemListOrder: "https://schema.org/ItemListOrderAscending",
    numberOfItems: items.length,
    itemListElement: items,
  };
}

export default function ProductJsonLd() {
  const { products } = useProducts();

  useEffect(() => {
    if (!products || products.length === 0) return undefined;

    const id = "ld-json-products";
    let el = document.getElementById(id);
    if (!el) {
      el = document.createElement("script");
      el.type = "application/ld+json";
      el.id = id;
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(buildPayload(products));

    return () => {
      const existing = document.getElementById(id);
      if (existing && existing.parentNode) existing.parentNode.removeChild(existing);
    };
  }, [products]);

  return null;
}
